import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { isDefined, openUrl } from "@/utils/utils";
import { ValidateErrorEntity } from "@rc-component/form/es/interface";
import { App as AntApp, Form } from "antd";
import { FormInstance } from "antd/lib/form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import {
  useAssignFinancialMediation,
  useChangeFinancialMediationVersion,
  useCreateFinancialMediation,
  useUpdateFinancialMediation,
  useValidateClient,
} from "../../../../common/api/queries";
import { Permission } from "../../../../common/security/enums";
import { hasPermission } from "../../../../common/security/utils";
import { FieldConstraintViolation } from "../../../../common/types";
import { usePermissions } from "../../../auth/hooks";
import { CalcValidationGroup, ClientType } from "../../../client/enums";
import { Client, CreateUpdateContractClient } from "../../../client/types";
import { clientToCreateUpdateContractClient, processClientsDataViolations } from "../../../client/utils";
import { ProductFinancialSector } from "../../../product/enums";
import { PRODUCT_SECTOR_TO_UPDATE_CONTRACT_PERMISSION_MAP } from "../../../product/utils";
import {
  FinancialMediationStatus,
  FinancialMediationVersion,
  InvestmentQuestionnaireClientStatement,
  MediationClientFormType,
} from "../../enums";
import { FINANCIAL_MEDIATION_ROUTE_PATHS } from "../../paths";
import {
  AllFinancialMediationDataTypes,
  CreateUpdateFinancialMediation,
  DepositFinancialMediationData,
  DepositSpecificRequirement,
  FinancialMediation,
  FinancialMediationFormClients,
  FinancialMediationRecommendationData,
  LifeInsuranceFinancialMediationData,
  PillarsFinancialMediationData,
} from "../../types";
import {
  FINANCIAL_MEDIATION_CHILDREN_SECTORS,
  FINANCIAL_MEDIATION_CLIENTS_WITH_ID_CARD,
  FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS,
} from "../../utils";
import FinancialMediationAssignForm from "./FinancialMediationAssignForm";
import FinancialMediationFormClientDataSection from "./sections/FinancialMediationFormClientDataSection";
import { FinancialMediationFormClientsSection } from "./sections/FinancialMediationFormClientsSection";
import { FinancialMediationFormHeaderSection } from "./sections/FinancialMediationFormHeaderSection";
import FinancialMediationFormLifeQuestionnaireSection from "./sections/FinancialMediationFormLifeQuestionnaireSection";
import FinancialMediationFormOtherDataSection from "./sections/FinancialMediationFormOtherDataSection";
import FinancialMediationFormPillarsQuestionnaireSection from "./sections/FinancialMediationFormPillarsQuestionnaireSection";
import { FinancialMediationFormRecommendationsSection } from "./sections/FinancialMediationFormRecommendationsSection";
import FinancialMediationFormRequirementsSection from "./sections/FinancialMediationFormRequirementsSection";

const createFormClients = (mediation?: FinancialMediation): FinancialMediationFormClients => ({
  policyHolder: mediation?.clients[mediation.policyHolderIndex],
  secondaryClient: isDefined(mediation?.secondaryClientIndex)
    ? mediation?.clients[mediation.secondaryClientIndex]
    : undefined,
  policyHolderRepresentative: isDefined(mediation?.policyHolderRepresentativeIndex)
    ? mediation?.clients[mediation.policyHolderRepresentativeIndex]
    : undefined,
  secondaryClientRepresentative: isDefined(mediation?.secondaryClientRepresentativeIndex)
    ? mediation?.clients[mediation.secondaryClientRepresentativeIndex]
    : undefined,
  child1: isDefined(mediation?.child1Index) ? mediation?.clients[mediation.child1Index] : undefined,
  child2: isDefined(mediation?.child2Index) ? mediation?.clients[mediation.child2Index] : undefined,
  child3: isDefined(mediation?.child3Index) ? mediation?.clients[mediation.child3Index] : undefined,
  child4: isDefined(mediation?.child4Index) ? mediation?.clients[mediation.child4Index] : undefined,
});

type Props = {
  financialMediation?: FinancialMediation;
};

export const FinancialMediationForm = ({ financialMediation }: Props) => {
  const { t } = useTranslation();
  const { modal, notification } = AntApp.useApp();

  const [form] = Form.useForm<CreateUpdateFinancialMediation>();
  const sector = Form.useWatch("sector", form);
  const validateAllFields = Form.useWatch("validateAllFields", form);

  const createFinancialMediation = useCreateFinancialMediation();
  const updateFinancialMediation = useUpdateFinancialMediation();
  const changeFinancialMediationVersion = useChangeFinancialMediationVersion();
  const assignFinancialMediation = useAssignFinancialMediation();

  const permissions = usePermissions();

  const clientValidation = useValidateClient();

  const [formValuesChanged, setFormValuesChanged] = useState(false);

  const [clients, setClients] = useState<FinancialMediationFormClients>(() => createFormClients(financialMediation));
  const [clientsViolationErrors, setClientsViolationErrors] = useState<
    Map<MediationClientFormType, FieldConstraintViolation[]>
  >(() => new Map());

  const [secondaryClientActive, setSecondaryClientActive] = useState(
    () =>
      isDefined(financialMediation?.secondaryClientIndex) &&
      !!financialMediation?.clients[financialMediation.secondaryClientIndex],
  );

  const [childrenCount, setChildrenCount] = useState<number>(
    [
      financialMediation?.child1Index,
      financialMediation?.child2Index,
      financialMediation?.child3Index,
      financialMediation?.child4Index,
    ].filter((i) => isDefined(i)).length,
  );

  const [representative1Active, setRepresentative1Active] = useState<boolean>(() => {
    const policyHolderType = financialMediation?.clients[financialMediation.policyHolderIndex]?.type;

    return (
      isDefined(financialMediation?.policyHolderRepresentativeIndex) &&
      !!financialMediation?.clients[financialMediation.policyHolderRepresentativeIndex] &&
      (policyHolderType === ClientType.NATURAL || policyHolderType === ClientType.SELF_EMPLOYED)
    );
  });

  const [representative2Active, setRepresentative2Active] = useState<boolean>(() => {
    const secondaryClientType = isDefined(financialMediation?.secondaryClientIndex)
      ? financialMediation?.clients[financialMediation.secondaryClientIndex]?.type
      : undefined;

    return (
      isDefined(financialMediation?.secondaryClientRepresentativeIndex) &&
      !!financialMediation?.clients[financialMediation.secondaryClientRepresentativeIndex] &&
      (secondaryClientType === ClientType.NATURAL || secondaryClientType === ClientType.SELF_EMPLOYED)
    );
  });

  const [assignFormOpen, setAssignFormOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!financialMediation) {
      return;
    }

    const { policyHolderData, secondaryClientData, investmentQuestionnaireData, ...otherData } =
      financialMediation.mediationData as AllFinancialMediationDataTypes;

    const initValues = {
      sector: financialMediation.sector,
      mediationData: {
        ...otherData,
        policyHolderData: {
          ...policyHolderData,
          refuseToProvideFinancialSituationInfo: !!policyHolderData.refuseToProvideFinancialSituationInfo,
        },
        secondaryClientData: secondaryClientData
          ? {
              ...secondaryClientData,
              refuseToProvideFinancialSituationInfo: !!secondaryClientData.refuseToProvideFinancialSituationInfo,
            }
          : null,
        investmentQuestionnaireData: investmentQuestionnaireData
          ? {
              ...investmentQuestionnaireData,
              refuseToAnswerQuestions1To9: !!investmentQuestionnaireData.refuseToAnswerQuestions1To9,
              refuseToAnswerQuestion10: !!investmentQuestionnaireData.refuseToAnswerQuestion10,
              refuseToAnswerQuestions11To13: !!investmentQuestionnaireData.refuseToAnswerQuestions11To13,
              refuseToAnswerQuestions15To16: !!investmentQuestionnaireData.refuseToAnswerQuestions15To16,
              refuseToAnswerQuestions1To7: !!investmentQuestionnaireData.refuseToAnswerQuestions1To7,
              refuseToAnswerQuestions8To10: !!investmentQuestionnaireData.refuseToAnswerQuestions8To10,
              refuseToAnswerQuestions12To13: !!investmentQuestionnaireData.refuseToAnswerQuestions12To13,
            }
          : null,
        signDate: otherData.signDate,
      } as AllFinancialMediationDataTypes,
      policyHolderIdentifier: financialMediation.clients[financialMediation.policyHolderIndex]?.identifier,
      secondaryClientIdentifier: isDefined(financialMediation.secondaryClientIndex)
        ? financialMediation.clients[financialMediation.secondaryClientIndex]?.identifier
        : undefined,
      policyHolderRepresentativeIdentifier: isDefined(financialMediation.policyHolderRepresentativeIndex)
        ? financialMediation.clients[financialMediation.policyHolderRepresentativeIndex]?.identifier
        : undefined,
      secondaryClientRepresentativeIdentifier: financialMediation.secondaryClientRepresentativeIndex
        ? financialMediation.clients[financialMediation.secondaryClientRepresentativeIndex]?.identifier
        : undefined,
      child1Identifier: financialMediation.child1Index
        ? financialMediation.clients[financialMediation.child1Index]?.identifier
        : undefined,
      child2Identifier: financialMediation.child2Index
        ? financialMediation.clients[financialMediation.child2Index]?.identifier
        : undefined,
      child3Identifier: financialMediation.child3Index
        ? financialMediation.clients[financialMediation.child3Index]?.identifier
        : undefined,
      child4Identifier: financialMediation.child4Index
        ? financialMediation.clients[financialMediation.child4Index]?.identifier
        : undefined,
      child1RepresentativeIdentifier: financialMediation.child1RepresentativeIndex
        ? financialMediation.clients[financialMediation.child1RepresentativeIndex]?.identifier
        : undefined,
      child2RepresentativeIdentifier: financialMediation.child2RepresentativeIndex
        ? financialMediation.clients[financialMediation.child2RepresentativeIndex]?.identifier
        : undefined,
      child3RepresentativeIdentifier: financialMediation.child3RepresentativeIndex
        ? financialMediation.clients[financialMediation.child3RepresentativeIndex]?.identifier
        : undefined,
      child4RepresentativeIdentifier: financialMediation.child4RepresentativeIndex
        ? financialMediation.clients[financialMediation.child4RepresentativeIndex]?.identifier
        : undefined,
      validateAllFields: false,
    };

    if (financialMediation.sector === ProductFinancialSector.DEPOSITS) {
      (initValues.mediationData as DepositFinancialMediationData).clientSpecificRequirements = (
        (otherData?.clientSpecificRequirements || []) as DepositSpecificRequirement[]
      ).map((r) => ({ ...r, selected: !!r?.selected }));
    }

    form.setFieldsValue(initValues);
    setClients(createFormClients(financialMediation));
  }, [financialMediation]);

  const showSecondaryClient =
    secondaryClientActive && !!sector && FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector);

  const handleClientChange = (type: MediationClientFormType, client?: Partial<Client>) => {
    setFormValuesChanged(true);

    if (client) {
      const clients = setClient(client, type);
      clientValidation.mutate(
        {
          prefix: `clients[${createClientsIndicesMap(clients).get(type)}]`,
          client: clientToCreateUpdateContractClient(client),
          validationGroup: FINANCIAL_MEDIATION_CLIENTS_WITH_ID_CARD.includes(type)
            ? CalcValidationGroup.GENERATE
            : undefined,
        },
        {
          onError: (error) => {
            if (error.violations) {
              setClientsViolationErrors(
                new Map([
                  ...clientsViolationErrors,
                  ...processClientsDataViolations(createClientsIndicesMap(clients), "clients", error.violations),
                ]),
              );
            }
          },
        },
      );
    } else {
      switch (type) {
        case MediationClientFormType.POLICY_HOLDER:
          form.setFieldsValue({
            mediationData: { policyHolderData: { email: undefined, phone: undefined, marketingConsent: undefined } },
          });
          break;
        case MediationClientFormType.SECONDARY_CLIENT:
          form.setFieldsValue({
            mediationData: { secondaryClientData: { email: undefined, phone: undefined, marketingConsent: undefined } },
          });
          break;
      }
    }
  };

  const handleClientViolationErrorsChange = (
    type: MediationClientFormType,
    violations?: FieldConstraintViolation[],
  ) => {
    const updatedViolationsErrors = new Map<MediationClientFormType, FieldConstraintViolation[]>([
      ...clientsViolationErrors,
    ]);
    if (violations?.length) {
      updatedViolationsErrors.set(type, violations);
    } else {
      updatedViolationsErrors.delete(type);
    }
    setClientsViolationErrors(updatedViolationsErrors);
  };

  const handleSaveError = (violations?: FieldConstraintViolation[]) => {
    setErrorsToForm(form, "financialMediation.attrs", violations);

    const clientsViolations = violations?.filter((violation) => violation.fieldPath.startsWith("clients"));

    if (clientsViolations?.length) {
      setClientsViolationErrors(
        (prevState) =>
          new Map([
            ...prevState,
            ...processClientsDataViolations(createClientsIndicesMap(clients), "clients", clientsViolations),
          ]),
      );
    }
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const clientsIndices = createClientsIndicesMap(clients);
        const processedClients = createContractClientsArray(clients);
        const clientsViolationErrorsKeys = [...clientsViolationErrors.keys()];

        if ([...clientsIndices.keys()].some((clientType) => clientsViolationErrorsKeys.includes(clientType))) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else {
          const processedData = { ...values.mediationData } as AllFinancialMediationDataTypes;

          processedData.childrenRelationships = processedData.childrenRelationships || [];
          processedData.generalAttachments = processedData.generalAttachments || [];
          processedData.sectorAttachments = processedData.sectorAttachments || [];
          processedData.recommendationData = {
            ...(processedData.recommendationData as FinancialMediationRecommendationData),
            recommendations: processedData.recommendationData?.recommendations || [],
          };

          if (
            (sector === ProductFinancialSector.LIFE_INSURANCES ||
              sector === ProductFinancialSector.SENIOR_PENSION_SAVINGS ||
              sector === ProductFinancialSector.SUPPLEMENTARY_PENSION_SAVINGS) &&
            processedData.investmentQuestionnaireData?.clientStatement ===
              InvestmentQuestionnaireClientStatement.WILLING_TO_ANSWER
          ) {
            processedData.investmentQuestionnaireData = {
              ...processedData.investmentQuestionnaireData,
              informedAboutRisks: processedData.investmentQuestionnaireData.informedAboutRisks || [],
            };
          }

          switch (sector) {
            case ProductFinancialSector.NON_LIFE_INSURANCES:
              processedData.clientRequirements = processedData.clientRequirements || [];
              break;
            case ProductFinancialSector.LIFE_INSURANCES:
              processedData.clientRequirements = processedData.clientRequirements || [];
              processedData.clientRiskRequirements = processedData.clientRiskRequirements || [];
              break;
            case ProductFinancialSector.DEPOSITS:
              processedData.clientRequirements = processedData.clientRequirements || [];
              processedData.clientSpecificRequirements = processedData.clientSpecificRequirements || [];
              break;
            case ProductFinancialSector.SENIOR_PENSION_SAVINGS:
            case ProductFinancialSector.SUPPLEMENTARY_PENSION_SAVINGS:
              processedData.clientRequirements = processedData.clientRequirements || [];
              break;
          }

          const processedValues = {
            ...values,
            optimisticLockVersion: financialMediation?.optimisticLockVersion,
            status: financialMediation
              ? values.validateAllFields
                ? FinancialMediationStatus.FINISHED
                : FinancialMediationStatus.UNFINISHED
              : undefined,
            sector: financialMediation ? undefined : values.sector,
            mediationData: processedData,
            clients: processedClients,
            policyHolderIndex: clientsIndices.get(MediationClientFormType.POLICY_HOLDER),
            secondaryClientIndex: clientsIndices.get(MediationClientFormType.SECONDARY_CLIENT),
            policyHolderRepresentativeIndex: clientsIndices.get(MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE),
            secondaryClientRepresentativeIndex: clientsIndices.get(
              MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE,
            ),
            child1Index: clientsIndices.get(MediationClientFormType.CHILD_1),
            child2Index: clientsIndices.get(MediationClientFormType.CHILD_2),
            child3Index: clientsIndices.get(MediationClientFormType.CHILD_3),
            child4Index: clientsIndices.get(MediationClientFormType.CHILD_4),
            child1RepresentativeIndex: resolveClientIndex(processedClients, values.child1RepresentativeIdentifier),
            child2RepresentativeIndex: resolveClientIndex(processedClients, values.child2RepresentativeIdentifier),
            child3RepresentativeIndex: resolveClientIndex(processedClients, values.child3RepresentativeIdentifier),
            child4RepresentativeIndex: resolveClientIndex(processedClients, values.child4RepresentativeIdentifier),
            policyHolderIdentifier: undefined,
            secondaryClientIdentifier: undefined,
            policyHolderRepresentativeIdentifier: undefined,
            secondaryClientRepresentativeIdentifier: undefined,
            child1Identifier: undefined,
            child2Identifier: undefined,
            child3Identifier: undefined,
            child4Identifier: undefined,
            child1RepresentativeIdentifier: undefined,
            child2RepresentativeIdentifier: undefined,
            child3RepresentativeIdentifier: undefined,
            child4RepresentativeIdentifier: undefined,
            validateAllFields: undefined,
          } as CreateUpdateFinancialMediation;

          if (values.validateAllFields) {
            modal.confirm({
              title: t("financialMediation.helpers.finishConfirm"),
              okText: t("common.yes"),
              cancelText: t("common.no"),
              onOk: () => {
                if (financialMediation?.id) {
                  updateFinancialMediation.mutate(
                    { id: financialMediation.id, object: processedValues },
                    {
                      onSuccess: () => {
                        if (formValuesChanged) {
                          setFormValuesChanged(false);
                        }
                      },
                      onError: (error) => handleSaveError(error.violations),
                    },
                  );
                }
              },
            });
          } else {
            if (financialMediation?.id) {
              updateFinancialMediation.mutate?.(
                { id: financialMediation.id, object: processedValues },
                {
                  onSuccess: () => {
                    if (formValuesChanged) {
                      setFormValuesChanged(false);
                    }
                  },
                  onError: (error) => handleSaveError(error.violations),
                },
              );
            } else {
              createFinancialMediation.mutate(processedValues, {
                onError: (error) => handleSaveError(error.violations),
              });
            }
          }
        }
      })
      .catch((errors: ValidateErrorEntity) => {
        notification.error({
          title: t("common.error"),
          description: t("common.validations.formError"),
        });
        resolveFormValidationError(errors);
      })
      .finally(() => {
        if (validateAllFields) {
          form.setFieldsValue({ validateAllFields: false });
        }
      });
  };

  const handleFormValuesChange = (changedValues: Partial<CreateUpdateFinancialMediation>) => {
    if (changedValues && !formValuesChanged) {
      setFormValuesChanged(true);
    }
  };

  const handleSecondaryClientActiveToggle = () => {
    setSecondaryClientActive(!secondaryClientActive);
    setFormValuesChanged(true);
  };

  const handleChildAdd = () => {
    setChildrenCount(childrenCount + 1);
    setFormValuesChanged(true);
  };

  const handleChildRemove = () => {
    setChildrenCount(childrenCount - 1);
    setFormValuesChanged(true);
  };

  const handleRepresentative1Toggle = () => {
    setRepresentative1Active(!representative1Active);
    setFormValuesChanged(true);
  };

  const handleRepresentative2Toggle = () => {
    setRepresentative2Active(!representative2Active);
    setFormValuesChanged(true);
  };

  const handleGenerateClick = (version: FinancialMediationVersion) => {
    if (!financialMediation) {
      return;
    }
    const generateCallback = () =>
      openUrl(
        window.location.origin +
          generatePath(FINANCIAL_MEDIATION_ROUTE_PATHS.file.to, { id: financialMediation.id }) +
          `?version=${version}`,
        "_blank",
      );

    if (financialMediation.version === version) {
      executeIfFormValuesNotChanged(generateCallback);
    } else {
      generateCallback();
    }
  };

  const handleChangeVersionClick = () => {
    executeIfFormValuesNotChanged(() => {
      if (financialMediation) {
        changeFinancialMediationVersion.mutate({
          id: financialMediation.id,
          object: { optimisticLockVersion: financialMediation.optimisticLockVersion },
        });
      }
    });
  };

  const handleFinishClick = () => {
    executeIfFormValuesNotChanged(() => form.setFieldsValue({ validateAllFields: true }));
  };

  const handleAssignClick = (status: FinancialMediationStatus) => {
    switch (status) {
      case FinancialMediationStatus.ASSIGNED_TO_CONTRACT:
        setAssignFormOpen(true);
        break;
      case FinancialMediationStatus.ASSIGNED_TO_CLIENT:
        executeIfFormValuesNotChanged(() =>
          modal.confirm({
            title: t("financialMediation.helpers.finishWithoutContractConfirm"),
            okText: t("common.yes"),
            cancelText: t("common.no"),
            onOk: () => {
              if (financialMediation) {
                assignFinancialMediation.mutate({
                  id: financialMediation.id,
                  object: { optimisticLockVersion: financialMediation.optimisticLockVersion, status },
                });
              }
            },
          }),
        );
        break;
    }
  };

  const executeIfFormValuesNotChanged = (callback: () => void) => {
    if (formValuesChanged) {
      notification.warning({
        title: t("common.warning"),
        description: t("financialMediation.validations.unsavedChanges"),
      });
    } else {
      callback();
    }
  };

  const setClient = (client: Partial<Client>, type: MediationClientFormType) => {
    let updatedClients: FinancialMediationFormClients;

    switch (type) {
      case MediationClientFormType.POLICY_HOLDER:
        updatedClients = { ...clients, policyHolder: client };
        break;
      case MediationClientFormType.SECONDARY_CLIENT:
        updatedClients = { ...clients, secondaryClient: client };
        break;
      case MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE:
        updatedClients = { ...clients, policyHolderRepresentative: client };
        break;
      case MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE:
        updatedClients = { ...clients, secondaryClientRepresentative: client };
        break;
      case MediationClientFormType.CHILD_1:
        updatedClients = { ...clients, child1: client };
        break;
      case MediationClientFormType.CHILD_2:
        updatedClients = { ...clients, child2: client };
        break;
      case MediationClientFormType.CHILD_3:
        updatedClients = { ...clients, child3: client };
        break;
      case MediationClientFormType.CHILD_4:
        updatedClients = { ...clients, child4: client };
        break;
    }

    setClients(updatedClients);
    return updatedClients;
  };

  const createClientsIndicesMap = (clients: FinancialMediationFormClients) => {
    const {
      policyHolder,
      secondaryClient,
      policyHolderRepresentative,
      secondaryClientRepresentative,
      child1,
      child2,
      child3,
      child4,
    } = clients;
    const indices = new Map<MediationClientFormType, number>();
    let index = 0;

    if (policyHolder) {
      indices.set(MediationClientFormType.POLICY_HOLDER, index++);
    }

    if (
      sector &&
      FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector) &&
      secondaryClientActive &&
      secondaryClient
    ) {
      indices.set(MediationClientFormType.SECONDARY_CLIENT, index++);
    }

    if (
      policyHolder &&
      policyHolderRepresentative &&
      (representative1Active || policyHolder.type === ClientType.LEGAL)
    ) {
      indices.set(MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE, index++);
    }

    if (
      sector &&
      FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector) &&
      secondaryClientActive &&
      secondaryClient &&
      secondaryClientRepresentative &&
      (representative2Active || secondaryClient.type === ClientType.LEGAL)
    ) {
      indices.set(MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE, index++);
    }

    if (
      sector &&
      FINANCIAL_MEDIATION_CHILDREN_SECTORS.includes(sector) &&
      (policyHolder?.type === ClientType.NATURAL || secondaryClient?.type === ClientType.NATURAL)
    ) {
      if (childrenCount >= 1 && child1) {
        indices.set(MediationClientFormType.CHILD_1, index++);
      }
      if (childrenCount >= 2 && child2) {
        indices.set(MediationClientFormType.CHILD_2, index++);
      }
      if (childrenCount >= 3 && child3) {
        indices.set(MediationClientFormType.CHILD_3, index++);
      }
      if (childrenCount >= 4 && child4) {
        indices.set(MediationClientFormType.CHILD_4, index);
      }
    }

    return indices;
  };

  const createContractClientsArray = (clients: FinancialMediationFormClients) => {
    const {
      policyHolder,
      secondaryClient,
      policyHolderRepresentative,
      secondaryClientRepresentative,
      child1,
      child2,
      child3,
      child4,
    } = clients;
    const createUpdateClients: CreateUpdateContractClient[] = [];

    if (policyHolder) {
      createUpdateClients.push(clientToCreateUpdateContractClient(policyHolder));
    }

    if (
      sector &&
      FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector) &&
      secondaryClientActive &&
      secondaryClient
    ) {
      createUpdateClients.push(clientToCreateUpdateContractClient(secondaryClient));
    }

    if (
      policyHolder &&
      policyHolderRepresentative &&
      (representative1Active || policyHolder.type === ClientType.LEGAL)
    ) {
      createUpdateClients.push(clientToCreateUpdateContractClient(policyHolderRepresentative));
    }

    if (
      sector &&
      FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector) &&
      secondaryClientActive &&
      secondaryClient &&
      secondaryClientRepresentative &&
      (representative2Active || secondaryClient.type === ClientType.LEGAL)
    ) {
      createUpdateClients.push(clientToCreateUpdateContractClient(secondaryClientRepresentative));
    }

    if (
      sector &&
      FINANCIAL_MEDIATION_CHILDREN_SECTORS.includes(sector) &&
      (policyHolder?.type === ClientType.NATURAL || secondaryClient?.type === ClientType.NATURAL)
    ) {
      if (childrenCount >= 1 && child1) {
        createUpdateClients.push(clientToCreateUpdateContractClient(child1));
      }
      if (childrenCount >= 2 && child2) {
        createUpdateClients.push(clientToCreateUpdateContractClient(child2));
      }
      if (childrenCount >= 3 && child3) {
        createUpdateClients.push(clientToCreateUpdateContractClient(child3));
      }
      if (childrenCount >= 4 && child4) {
        createUpdateClients.push(clientToCreateUpdateContractClient(child4));
      }
    }

    return createUpdateClients;
  };

  const resolveClientIndex = (clients: CreateUpdateContractClient[], identifier?: string) => {
    if (identifier) {
      const index = clients.findIndex(
        (c) => "personalIdentificationNumber" in c.client && c.client.personalIdentificationNumber === identifier,
      );
      return index === -1 ? undefined : index;
    }
    return undefined;
  };

  useEffect(() => {
    if (validateAllFields) {
      handleFormSubmit();
    }
  }, [validateAllFields]);

  const formDisabled =
    financialMediation &&
    (financialMediation.status !== FinancialMediationStatus.UNFINISHED ||
      (sector &&
        !hasPermission(permissions, PRODUCT_SECTOR_TO_UPDATE_CONTRACT_PERMISSION_MAP.get(sector) as Permission)));

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        name="financialMediationForm"
        disabled={formDisabled}
        onValuesChange={handleFormValuesChange}
      >
        <FinancialMediationFormHeaderSection
          form={form}
          financialMediation={financialMediation}
          permissions={permissions}
          onFormSubmit={handleFormSubmit}
          onGenerateClick={handleGenerateClick}
          onChangeVersionClick={handleChangeVersionClick}
          onFinishClick={handleFinishClick}
          onAssignClick={handleAssignClick}
        />

        {sector ? (
          <>
            <FinancialMediationFormClientsSection
              form={form}
              formDisabled={formDisabled}
              sector={sector}
              clients={clients}
              clientsViolationErrors={clientsViolationErrors}
              secondaryClientActive={secondaryClientActive}
              childrenCount={childrenCount}
              representative1Active={representative1Active}
              representative2Active={representative2Active}
              validateAllFields={validateAllFields}
              onClientChange={handleClientChange}
              onClientViolationErrorsChange={handleClientViolationErrorsChange}
              onSecondaryClientActiveToggle={handleSecondaryClientActiveToggle}
              onChildAdd={handleChildAdd}
              onChildRemove={handleChildRemove}
              onRepresentative1Toggle={handleRepresentative1Toggle}
              onRepresentative2Toggle={handleRepresentative2Toggle}
              onClientFormValuesChange={() => setFormValuesChanged(true)}
            />

            <FinancialMediationFormClientDataSection
              form={form}
              sector={sector}
              showSecondaryClient={showSecondaryClient}
              validateAllFields={validateAllFields}
            />

            <FinancialMediationFormRequirementsSection
              sector={sector}
              showSecondaryClient={showSecondaryClient}
              validateAllFields={validateAllFields}
            />

            {sector === ProductFinancialSector.LIFE_INSURANCES && (
              <FinancialMediationFormLifeQuestionnaireSection
                form={form as FormInstance<CreateUpdateFinancialMediation<LifeInsuranceFinancialMediationData>>}
                validateAllFields={validateAllFields}
              />
            )}

            {(sector === ProductFinancialSector.SENIOR_PENSION_SAVINGS ||
              sector === ProductFinancialSector.SUPPLEMENTARY_PENSION_SAVINGS) && (
              <FinancialMediationFormPillarsQuestionnaireSection
                form={form as FormInstance<CreateUpdateFinancialMediation<PillarsFinancialMediationData>>}
                validateAllFields={validateAllFields}
              />
            )}

            <FinancialMediationFormRecommendationsSection
              form={form}
              formDisabled={formDisabled}
              sector={sector}
              validateAllFields={validateAllFields}
            />

            <FinancialMediationFormOtherDataSection
              formDisabled={formDisabled}
              sector={sector}
              showSecondaryClient={showSecondaryClient}
              validateAllFields={validateAllFields}
              onFormSubmit={handleFormSubmit}
            />
          </>
        ) : (
          <div className="center-align sub-header-info dashed">{t("financialMediation.helpers.noSectorSelected")}</div>
        )}
      </Form>

      {(financialMediation?.status === FinancialMediationStatus.FINISHED ||
        financialMediation?.status === FinancialMediationStatus.ASSIGNED_TO_CONTRACT) && (
        <FinancialMediationAssignForm
          open={assignFormOpen}
          financialMediation={financialMediation}
          onFormCancel={() => setAssignFormOpen(false)}
        />
      )}
    </>
  );
};
